import { pick } from "./Obj";
/**
 * DOM utils
 */
var isReadyPromise = null;
var idCounter = 0;
/**
 * isReady
 * When DOMContent is loaded the promise will be resolved
 * @return  {object}    Promise
 */
export var isReady = function () {
    if (!isReadyPromise) {
        isReadyPromise = new Promise(function (resolve) {
            if (/loaded|complete/.test(document.readyState)) {
                resolve();
            }
            else {
                on(window, "load", resolve);
                on(document, "DOMContentLoaded", resolve);
                on(document, "onreadystatechange", resolve);
            }
        });
    }
    return isReadyPromise;
};
/**
 * on
 * Add event listener cross browser compatible...
 * @param   {object}    element
 * @param   {string}    event name
 * @param   {function}  handler
 * @return  {function}  returns a function wich allows you to detach the listener
 */
export var on = function (el, name, handler, options) {
    if (options === void 0) { options = {}; }
    if (el.addEventListener) {
        el.addEventListener(name, handler, options);
    }
    else if (el.attachEvent) {
        el.attachEvent("on" + name, handler);
    }
    else {
        el["on" + name] = handler;
    }
    return function () { return off(el, name, handler, options); };
};
/**
 * off
 * Remove event listener cross browser compatible...
 * @param   {object}    element
 * @param   {string}    event name
 * @param   {function}  handler
 */
export var off = function (el, name, handler, options) {
    if (options === void 0) { options = {}; }
    if (el.addEventListener) {
        el.removeEventListener(name, handler, options);
    }
    else if (el.detachEvent) {
        el.detachEvent("on" + name, handler);
    }
    else {
        el["on" + name] = null;
    }
};
/**
 * Optimized function to test whether an element is in the document or not.
 * @info https://stackoverflow.com/questions/11943441/how-can-i-tell-if-a-node-is-in-memory-or-in-the-dom
 * @param element
 */
export var isElementInDocument = function (element, documentElement) {
    if (documentElement === void 0) { documentElement = document; }
    if (!element || !element.parentNode) {
        return false;
    }
    if (element.contains) {
        return documentElement.body.contains(element);
    }
    var id = element.id || uniqueId();
    element.id = id;
    return documentElement.getElementById(id) !== null;
};
/**
 * Cross browser is page hidden implementation.
 * Page visibility API
 */
export var isPageHidden = function (forcedWindow) {
    var doc = (forcedWindow || window).document;
    return doc.hidden || doc.msHidden || doc.webkitHidden;
};
/**
 * Cross browser implementation
 * @note - can be extended when required...
 */
export var getReadyState = function (forcedWindow) {
    var win = forcedWindow || window;
    return win && win.document && win.document.readyState ? win.document.readyState : undefined;
};
/**
 * getComputedStyle
 * Cross browser implementation
 */
export var getComputedStyle = function (el, forcedWindow) {
    var win = forcedWindow || window;
    return win.getComputedStyle ? win.getComputedStyle(el) : el.currentStyle;
};
/**
 * Helper to obtain a single or a selection of computed style properties
 */
export var getComputedStyleProperties = function (el, properties) {
    return pick(getComputedStyle(el), properties);
};
/**
 * getOuterHeight
 * Get height including the margin.
 *
 * If margin should not be taken into account, use box-sizing: border-box and then
 * offsetHeight.
 * @note - So only use this function when you need height including margins.
 * @param el
 * @param heightProperty
 */
export var getOuterHeight = function (el, heightProperty) {
    if (heightProperty === void 0) { heightProperty = "scrollHeight"; }
    var styles = getComputedStyleProperties(el, ["marginTop", "marginBottom"]);
    return Math.ceil(el[heightProperty] + parseFloat(styles.marginTop) + parseFloat(styles.marginBottom));
};
/**
 * getOuterWidth
 * Get width including the margin.
 *
 * If margin should not be taken into account, use box-sizing: border-box and then
 * offsetWidth.
 * @note - So only use this function when you need width including margins.
 * @param el
 * @param widthProperty
 */
export var getOuterWidth = function (el, widthProperty) {
    if (widthProperty === void 0) { widthProperty = "scrollWidth"; }
    var styles = getComputedStyleProperties(el, ["marginLeft", "marginRight"]);
    return Math.ceil(el[widthProperty] + parseFloat(styles.marginLeft) + parseFloat(styles.marginRight));
};
/**
 * getViewportInnerWidth
 * Cross-browser compatible method to detect viewport inner width
 */
export var getViewportInnerWidth = function (forcedWindow) {
    var win = forcedWindow || window;
    return win.innerWidth || win.document.documentElement.clientWidth || win.document.body.clientWidth || 0;
};
/**
 * getViewportInnerHeight
 * Cross-browser compatible method to detect viewport inner height
 */
export var getViewportInnerHeight = function (forcedWindow) {
    var win = forcedWindow || window;
    return win.innerHeight || win.document.documentElement.clientHeight || win.document.body.clientHeight || 0;
};
/**
 * @TODO - improve typing
 * @note - class should be passed as class instead of className
 * @param target
 * @param type
 * @param attrs
 */
export var createElement = function (target, type, attrs) {
    var element = document.createElement(type);
    Object.keys(attrs || {}).forEach(function (attr) {
        if (["function", "boolean"].indexOf(typeof attrs[attr]) !== -1) {
            element[attr] = attrs[attr];
        }
        else {
            element.setAttribute(attr, attrs[attr]);
        }
    });
    target.appendChild(element);
    return element;
};
/**
 * getNative
 * Prototypes can be overwritten and native functionality can be wrapped.
 * To be sure that you talk to a native implementation, this helper will create an iframe
 * (only once per pageload) and returns the provided function from the iframe.contentWindow.
 * Helper fn which creates an iframe and ret
 * @param name can be a string or a dot separated string to obtain nested functions (i.e console, history.pushState, etc.)
 * @return value the function or value that belongs to the provided name
 */
export var getNative = function (name) {
    var frame = getNative.prototype.frame;
    if (!frame && document.body) {
        var iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.ariaHidden = true;
        iframe.id = "hc__native-fn-restore";
        document.body.appendChild(iframe);
        getNative.prototype.frame = iframe;
        frame = iframe;
    }
    var activeWindow = !!frame ? frame.contentWindow : window;
    return name
        .split(".")
        .reduce(function (parent, ns) { return (parent && parent.hasOwnProperty && parent.hasOwnProperty(ns) ? parent[ns] : null); }, activeWindow);
};
/**
 * Helper method to create "unique" element id
 * @param prefix
 */
var uniqueId = function (prefix) {
    if (prefix === void 0) { prefix = ""; }
    idCounter++;
    return prefix + "rndm-el-" + idCounter;
};
/**
 * Detect if a HTML element is visible inside the window.
 * @param element
 */
var isElementInView = function (element, forcedWindow) {
    var win = forcedWindow || window;
    var rect = element.getBoundingClientRect();
    var top = rect.top;
    var bottom = rect.bottom;
    var left = rect.left;
    var right = rect.right;
    var isVisibleVertical = top >= 0 || bottom <= (win.innerHeight || win.document.documentElement.clientHeight);
    var isVisibleHorizontal = left >= 0 || right <= (win.innerWidth || win.document.documentElement.clientWidth);
    return isVisibleHorizontal && isVisibleVertical;
};
/**
 * Get the heigt of the visible part of the element in the viewport
 * @param rect
 */
var getElementVisibleHeight = function (rect, forcedWindow) {
    var win = forcedWindow || window;
    if (rect.x <= 0) {
        return rect.width + rect.x;
    }
    var windowWidth = win.innerWidth || win.document.documentElement.clientWidth;
    if (rect.width + rect.x < windowWidth) {
        return rect.width;
    }
    return rect.width - (rect.right - windowWidth);
};
/**
 * Get the width of the visible part of the element in the viewport
 * @param rect
 */
var getElementVisibleWidth = function (rect, forcedWindow) {
    var win = forcedWindow || window;
    if (rect.y <= 0) {
        return rect.height + rect.y;
    }
    var windowHeight = win.innerHeight || win.document.documentElement.clientHeight;
    if (rect.height + rect.y < windowHeight) {
        return rect.height;
    }
    return rect.height - (rect.bottom - windowHeight);
};
/**
 * Get the width of the visible part of the element in the viewport
 * @param rect
 */
export var isElementInViewport = function (element, percentage) {
    if (isElementInView(element)) {
        var rect = element.getBoundingClientRect();
        var visibileHeight = getElementVisibleHeight(rect);
        var visibileWidth = getElementVisibleWidth(rect);
        var acreage = rect.width * rect.height;
        var visibleAcreage = visibileHeight * visibileWidth;
        var visiblePercentage = (visibleAcreage / acreage) * 100;
        return visiblePercentage >= percentage;
    }
    return false;
};
/**
 * getWindow
 * Get global window object
 * https://github.com/Semantic-Org/Semantic-UI/issues/3855
 */
export var getWindow = function () {
    // tslint:disable-next-line: triple-equals
    return typeof window !== "undefined" && window.Math == Math
        ? window
        : // tslint:disable-next-line: triple-equals
            typeof self !== "undefined" && self.Math == Math
                ? self
                : // tslint:disable-next-line: function-constructor
                    Function("return this")();
};
/**
 * getRequestAnimationFrame
 */
export var getRequestAnimationFrame = function (forcedWindow) {
    var win = forcedWindow || getWindow();
    return (win.requestAnimationFrame ||
        win.mozRequestAnimationFrame ||
        win.webkitRequestAnimationFrame ||
        (function (fn) { return win.setTimeout(fn, 20); })).bind(win);
};
/**
 * getCancelAnimationFrame
 */
export var getCancelAnimationFrame = function (forcedWindow) {
    var win = forcedWindow || getWindow();
    return (win.cancelAnimationFrame ||
        win.mozCancelAnimationFrame ||
        win.webkitCancelAnimationFrame ||
        (function (timer) { return win.clearTimeout(timer); })).bind(win);
};
/**
 * applyStyle
 * Helper to apply style object to element
 * @param element
 * @param style
 */
export var applyStyle = function (element, style) {
    Object.keys(style).forEach(function (key) {
        element.style[key] = style[key];
    });
    return element;
};
//# sourceMappingURL=Dom.js.map